import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { createPortal } from 'react-dom';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {
  FiCheck,
  FiChevronDown,
  FiSearch,
  FiX
} = FiIcons;

const normalize = (value) =>
  String(value || '').toLowerCase();

const defaultGroup = (option) =>
  option.group || option.type || 'Other';

function HighlightedText({ value, query }) {
  const text = String(value || '');
  const normalizedQuery = query.trim();

  if (!normalizedQuery) {
    return text;
  }

  const index = normalize(text).indexOf(
    normalize(normalizedQuery)
  );

  if (index < 0) {
    return text;
  }

  return (
    <>
      {text.slice(0, index)}

      <mark className="rounded bg-blue-100 px-1 text-blue-800">
        {text.slice(
          index,
          index + normalizedQuery.length
        )}
      </mark>

      {text.slice(
        index + normalizedQuery.length
      )}
    </>
  );
}

export default function SearchableSelect({
  options = [],
  value = '',
  onChange,

  placeholder = 'Select an option',
  searchPlaceholder = 'Search...',
  emptyMessage = 'No matching options',

  className = '',

  disabled = false,
  loading = false,

  grouped = true,
  getGroup = defaultGroup,

  getLabel = (option) =>
    option.name ||
    option.label ||
    option.id,

  getSearchText = (option) => [
    option.name,
    option.label,
    option.code,
    option.type,
    option.sub_type,
    option.group
  ],

  renderOption,
  footer,

  menuWidth = 380,
  menuMaxWidth = 460,
  menuMaxHeight = 360,

  portalTarget = null
}) {
  const [isOpen, setIsOpen] =
    useState(false);

  const [query, setQuery] =
    useState('');

  const [highlightedIndex, setHighlightedIndex] =
    useState(0);

  const [popoverPosition, setPopoverPosition] =
    useState(null);

  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const popoverRef = useRef(null);
  const searchRef = useRef(null);
  const optionRefs = useRef([]);

  const resolvedPortalTarget =
    portalTarget ||
    (typeof document !== 'undefined'
      ? document.body
      : null);

  const selectedOption = useMemo(
    () =>
      options.find(
        (option) => option.id === value
      ),
    [options, value]
  );

  const filteredOptions = useMemo(() => {
    const normalizedQuery =
      normalize(query.trim());

    return options.filter((option) => {
      if (!normalizedQuery) {
        return true;
      }

      return getSearchText(option).some(
        (field) =>
          normalize(field).includes(
            normalizedQuery
          )
      );
    });
  }, [
    getSearchText,
    options,
    query
  ]);

  const groupedOptions = useMemo(() => {
    if (!grouped) {
      return [
        ['', filteredOptions]
      ];
    }

    const groups =
      filteredOptions.reduce(
        (result, option) => {
          const groupName =
            getGroup(option);

          if (!result[groupName]) {
            result[groupName] = [];
          }

          result[groupName].push(option);

          return result;
        },
        {}
      );

    return Object.entries(groups);
  }, [
    filteredOptions,
    getGroup,
    grouped
  ]);

  const flatOptions = useMemo(
    () =>
      groupedOptions.flatMap(
        ([, groupOptions]) =>
          groupOptions
      ),
    [groupedOptions]
  );

  const updatePopoverPosition =
    useCallback(() => {
      const trigger =
        triggerRef.current;

      if (!trigger) {
        return;
      }

      const triggerRect =
        trigger.getBoundingClientRect();

      const viewportPadding = 12;
      const gap = 8;

      const availableViewportWidth =
        window.innerWidth -
        viewportPadding * 2;

      const preferredPanelWidth =
        Math.max(
          triggerRect.width,
          menuWidth
        );

      const panelWidth =
        Math.min(
          preferredPanelWidth,
          menuMaxWidth,
          availableViewportWidth
        );

      const availableBelow =
        window.innerHeight -
        triggerRect.bottom -
        viewportPadding -
        gap;

      const availableAbove =
        triggerRect.top -
        viewportPadding -
        gap;

      const preferredPanelHeight =
        Math.min(
          menuMaxHeight,
          Math.max(
            220,
            popoverRef.current
              ?.offsetHeight ||
              menuMaxHeight
          )
        );

      const shouldOpenAbove =
        availableBelow <
          preferredPanelHeight &&
        availableAbove >
          availableBelow;

      const availableHeight =
        shouldOpenAbove
          ? availableAbove
          : availableBelow;

      const panelHeight =
        Math.min(
          preferredPanelHeight,
          Math.max(
            180,
            availableHeight
          )
        );

      let left = triggerRect.left;

      if (
        left + panelWidth >
        window.innerWidth -
          viewportPadding
      ) {
        left =
          triggerRect.right -
          panelWidth;
      }

      left = Math.max(
        viewportPadding,
        Math.min(
          left,
          window.innerWidth -
            panelWidth -
            viewportPadding
        )
      );

      const top = shouldOpenAbove
        ? Math.max(
            viewportPadding,
            triggerRect.top -
              panelHeight -
              gap
          )
        : triggerRect.bottom + gap;

      setPopoverPosition({
        top,
        left,
        width: panelWidth,
        maxHeight: panelHeight,
        opensAbove: shouldOpenAbove
      });
    }, [
      menuMaxHeight,
      menuMaxWidth,
      menuWidth
    ]);

  const close = useCallback(() => {
    setIsOpen(false);
    setQuery('');
    setPopoverPosition(null);
  }, []);

  const selectOption = (option) => {
    if (disabled || loading) {
      return;
    }

    onChange?.(
      option.id,
      option
    );

    close();
  };

  useEffect(() => {
    const handleOutsideInteraction =
      (event) => {
        const clickedTrigger =
          rootRef.current?.contains(
            event.target
          );

        const clickedPopover =
          popoverRef.current?.contains(
            event.target
          );

        if (
          !clickedTrigger &&
          !clickedPopover
        ) {
          close();
        }
      };

    document.addEventListener(
      'pointerdown',
      handleOutsideInteraction
    );

    return () => {
      document.removeEventListener(
        'pointerdown',
        handleOutsideInteraction
      );
    };
  }, [close]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const selectedIndex =
      flatOptions.findIndex(
        (option) =>
          option.id === value
      );

    setHighlightedIndex(
      selectedIndex >= 0
        ? selectedIndex
        : 0
    );

    const positionFrame =
      requestAnimationFrame(() => {
        updatePopoverPosition();
      });

    const focusFrame =
      requestAnimationFrame(() => {
        searchRef.current?.focus();
        searchRef.current?.select();
      });

    const handleViewportChange =
      () => {
        updatePopoverPosition();
      };

    window.addEventListener(
      'resize',
      handleViewportChange
    );

    window.addEventListener(
      'scroll',
      handleViewportChange,
      true
    );

    return () => {
      cancelAnimationFrame(
        positionFrame
      );

      cancelAnimationFrame(
        focusFrame
      );

      window.removeEventListener(
        'resize',
        handleViewportChange
      );

      window.removeEventListener(
        'scroll',
        handleViewportChange,
        true
      );
    };
  }, [
    flatOptions,
    isOpen,
    updatePopoverPosition,
    value
  ]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    requestAnimationFrame(
      updatePopoverPosition
    );
  }, [
    filteredOptions.length,
    isOpen,
    footer,
    updatePopoverPosition
  ]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    optionRefs.current[
      highlightedIndex
    ]?.scrollIntoView({
      block: 'nearest'
    });
  }, [
    highlightedIndex,
    isOpen
  ]);

  const handleKeyDown = (event) => {
    if (loading) {
      event.preventDefault();
      return;
    }

    if (!isOpen) {
      if (
        event.key === 'Enter' ||
        event.key === 'ArrowDown' ||
        event.key === ' '
      ) {
        event.preventDefault();

        setIsOpen(true);
      }

      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();

      setHighlightedIndex(
        (currentIndex) =>
          Math.min(
            currentIndex + 1,
            Math.max(
              flatOptions.length - 1,
              0
            )
          )
      );
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();

      setHighlightedIndex(
        (currentIndex) =>
          Math.max(
            currentIndex - 1,
            0
          )
      );
    }

    if (
      event.key === 'Enter' &&
      flatOptions[highlightedIndex]
    ) {
      event.preventDefault();

      selectOption(
        flatOptions[
          highlightedIndex
        ]
      );
    }

    if (event.key === 'Escape') {
      event.preventDefault();

      close();

      triggerRef.current?.focus();
    }

    if (event.key === 'Tab') {
      close();
    }
  };

  const dropdownContent = (
    <div
      ref={popoverRef}
      className={`
        ${
          portalTarget
            ? 'fixed z-[100000]'
            : 'fixed z-[9999]'
        }
        flex flex-col overflow-hidden
        rounded-md border border-slate-200
        bg-white shadow-2xl shadow-slate-900/15
      `}
      style={{
        top:
          popoverPosition?.top ??
          0,

        left:
          popoverPosition?.left ??
          0,

        width:
          popoverPosition?.width ??
          menuWidth,

        maxWidth:
          'calc(100vw - 24px)',

        maxHeight:
          popoverPosition?.maxHeight ??
          menuMaxHeight,

        visibility:
          popoverPosition
            ? 'visible'
            : 'hidden'
      }}
    >
      <div className="shrink-0 border-b border-slate-100 bg-white p-2">
        <div className="flex w-full items-center gap-2 rounded-md border border-slate-200 bg-white px-2.5 py-1 focus-within:border-blue-400 focus-within:ring-4 focus-within:ring-blue-500/10">
          <SafeIcon
            icon={FiSearch}
            className="shrink-0 text-sm text-slate-400"
          />
          <input
            ref={searchRef}
            type="search"
            value={query}
            onChange={(event) => {
              setQuery(
                event.target.value
              );
              setHighlightedIndex(0);
            }}
            onKeyDown={handleKeyDown}
            placeholder={searchPlaceholder}
            className="min-w-0 flex-1 bg-transparent text-sm text-slate-700 outline-none placeholder:text-slate-400"
          />
          {query && (
            <button
              type="button"
              onClick={() => {
                setQuery('');
                setHighlightedIndex(0);
                searchRef.current?.focus();
              }}
              className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
              aria-label="Clear search"
            >
              <SafeIcon
                icon={FiX}
                className="text-sm"
              />
            </button>
          )}
        </div>
      </div>

      <div
        className="min-h-0 flex-1 overflow-y-auto overscroll-contain p-1.5"
        role="listbox"
      >
        {groupedOptions.map(
          ([group, groupOptions]) => (
            <div
              key={group || 'options'}
            >
              {grouped && (
                <div className="sticky top-0 z-10 border-y border-slate-100 bg-slate-50/95 px-3 py-2 backdrop-blur-sm">
                  <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-slate-400">
                    {group}
                  </p>
                </div>
              )}

              <div className="py-1">
                {groupOptions.map(
                  (option) => {
                    const optionIndex =
                      flatOptions.findIndex(
                        (item) =>
                          item.id === option.id
                      );

                    const isSelected =
                      option.id === value;

                    const isHighlighted =
                      optionIndex ===
                      highlightedIndex;

                    const label =
                      getLabel(option);

                    return (
                      <button
                        ref={(element) => {
                          optionRefs.current[
                            optionIndex
                          ] = element;
                        }}
                        key={option.id}
                        type="button"
                        role="option"
                        aria-selected={isSelected}
                        title={String(label)}
                        onMouseEnter={() =>
                          setHighlightedIndex(
                            optionIndex
                          )
                        }
                        onClick={() =>
                          selectOption(option)
                        }
                        className={`flex w-full items-start justify-between gap-4 rounded-md px-3 py-1 text-left text-sm transition ${
                          isHighlighted
                            ? 'bg-blue-50'
                            : 'hover:bg-slate-50'
                        } ${
                          isSelected
                            ? 'font-semibold text-blue-700'
                            : 'font-normal text-slate-700'
                        }`}
                      >
                        <span className="min-w-0 flex-1 whitespace-normal break-words leading-5">
                          {renderOption ? (
                            renderOption(
                              option,
                              query
                            )
                          ) : (
                            <HighlightedText
                              value={label}
                              query={query}
                            />
                          )}
                        </span>

                        {isSelected && (
                          <SafeIcon
                            icon={FiCheck}
                            className="mt-0.5 shrink-0 text-blue-600"
                          />
                        )}
                      </button>
                    );
                  }
                )}
              </div>
            </div>
          )
        )}

        {filteredOptions.length === 0 && (
          <div className="px-4 py-10 text-center">
            <p className="text-sm font-medium text-slate-600">
              {emptyMessage}
            </p>

            <p className="mt-1 text-xs text-slate-400">
              Try another account name or code.
            </p>
          </div>
        )}
      </div>

      {footer && (
        <div className="shrink-0 border-t border-slate-100 bg-white">
          {footer}
        </div>
      )}
    </div>
  );

  return (
    <div
      ref={rootRef}
      className={`relative min-w-0 ${className}`}
    >
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled || loading}
        onClick={() => {
          if (
            disabled ||
            loading
          ) {
            return;
          }

          if (!isOpen) {
            setIsOpen(true);
          } else {
            close();
          }
        }}
        onKeyDown={handleKeyDown}
        className="flex w-full min-w-0 items-center justify-between gap-2 rounded-md border border-slate-200 bg-white px-3.5 py-1 text-left text-sm font-medium text-slate-800 shadow-sm outline-none transition hover:border-slate-300 hover:bg-slate-50 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 disabled:cursor-not-allowed disabled:opacity-50"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-busy={loading}
      >
        <span
          className={`min-w-0 flex-1 truncate ${
            selectedOption
              ? 'text-slate-800'
              : 'text-slate-400'
          }`}
          title={
            selectedOption
              ? String(
                  getLabel(selectedOption)
                )
              : placeholder
          }
        >
          {selectedOption
            ? getLabel(selectedOption)
            : placeholder}
        </span>

        {loading ? (
          <span className="flex shrink-0 items-center gap-1.5 text-xs font-medium text-blue-600">
            <span className="h-3 w-3 animate-spin rounded-full border-2 border-blue-200 border-t-blue-600" />
            <span>Saving...</span>
          </span>
        ) : (
          <SafeIcon
            icon={FiChevronDown}
            className={`shrink-0 text-slate-400 transition-transform ${
              isOpen
                ? 'rotate-180'
                : ''
            }`}
          />
        )}
      </button>

      {isOpen &&
        !loading &&
        resolvedPortalTarget &&
        createPortal(
          dropdownContent,
          resolvedPortalTarget
        )}
    </div>
  );
}